import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CustomCursor.css';

// SVG cursor as a data URL for the fountain pen (left-handed / mirrored)
const FOUNTAIN_PEN_CURSOR = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <!-- Pen body - mirrored for left-handed -->
  <path d="M 28 28 L 24 24 L 20 20 L 8 8 L 4 4" fill="none" stroke="#4a3728" stroke-width="1.5" stroke-linecap="round"/>
  <!-- Pen nib -->
  <path d="M 28 28 L 26 26 L 24 28 Z" fill="#8B7355"/>
  <!-- Pen barrel -->
  <path d="M 24 24 L 22 22 L 6 6 L 4 8 L 20 24 Z" fill="#5C4033"/>
  <!-- Pen grip -->
  <path d="M 22 22 L 20 20 L 18 22 L 20 24 Z" fill="#8B7355"/>
  <!-- Gold band -->
  <path d="M 18 18 L 17 17 L 15 19 L 16 20 Z" fill="#D4AF37"/>
  <!-- Pen cap -->
  <path d="M 8 8 L 4 4 L 2 6 L 6 10 Z" fill="#3d2817"/>
  <!-- Ink tip -->
  <circle cx="27" cy="27" r="1.5" fill="#2c2c2c"/>
</svg>
`)}`;

// Orange fall leaf cursor (left-handed / mirrored)
const LEAF_CURSOR = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <!-- Leaf shape - orange/red fall colors - mirrored for left-handed -->
  <path d="M 26 26 Q 24 16, 16 8 Q 8 8, 4 4 Q 8 12, 16 20 Q 24 28, 26 26" fill="#d4762c" stroke="#8B4513" stroke-width="1"/>
  <!-- Red/orange gradient patches -->
  <path d="M 22 22 Q 20 18, 16 14 Q 18 20, 22 22" fill="#c94c4c" opacity="0.6"/>
  <path d="M 14 12 Q 10 10, 6 6 Q 10 14, 14 16" fill="#e8a040" opacity="0.5"/>
  <!-- Leaf vein -->
  <path d="M 26 26 Q 18 18, 4 4" fill="none" stroke="#8B4513" stroke-width="1"/>
  <!-- Side veins -->
  <path d="M 22 22 Q 20 20, 18 18" fill="none" stroke="#8B4513" stroke-width="0.6" opacity="0.8"/>
  <path d="M 20 20 Q 18 18, 14 14" fill="none" stroke="#8B4513" stroke-width="0.6" opacity="0.8"/>
  <path d="M 16 16 Q 14 14, 10 10" fill="none" stroke="#8B4513" stroke-width="0.6" opacity="0.8"/>
  <!-- Stem -->
  <path d="M 26 26 L 28 28" stroke="#5C4033" stroke-width="1.5" stroke-linecap="round"/>
</svg>
`)}`;

// Array of fall leaf colors for the trail
const FALL_COLORS = [
  '#d4762c', // orange
  '#c94c4c', // red
  '#e8a040', // golden orange
  '#b8860b', // dark goldenrod
  '#cd853f', // peru/tan
  '#a0522d', // sienna
];

// Spring flower cursor (pink/magenta flower)
const FLOWER_CURSOR = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <!-- Stem -->
  <path d="M 16 32 Q 14 24, 16 16" stroke="#228B22" stroke-width="2" fill="none" stroke-linecap="round"/>
  <!-- Leaves on stem -->
  <path d="M 14 26 Q 10 24, 12 22 Q 14 24, 14 26" fill="#32CD32"/>
  <path d="M 18 22 Q 22 20, 20 18 Q 18 20, 18 22" fill="#32CD32"/>
  <!-- Flower petals -->
  <ellipse cx="16" cy="8" rx="4" ry="6" fill="#FF69B4" transform="rotate(0, 16, 12)"/>
  <ellipse cx="16" cy="8" rx="4" ry="6" fill="#FF1493" transform="rotate(72, 16, 12)"/>
  <ellipse cx="16" cy="8" rx="4" ry="6" fill="#FF69B4" transform="rotate(144, 16, 12)"/>
  <ellipse cx="16" cy="8" rx="4" ry="6" fill="#FF1493" transform="rotate(216, 16, 12)"/>
  <ellipse cx="16" cy="8" rx="4" ry="6" fill="#FF69B4" transform="rotate(288, 16, 12)"/>
  <!-- Flower center -->
  <circle cx="16" cy="12" r="4" fill="#FFD700"/>
  <circle cx="15" cy="11" r="1" fill="#FFA500"/>
  <circle cx="17" cy="11" r="1" fill="#FFA500"/>
  <circle cx="16" cy="13" r="1" fill="#FFA500"/>
</svg>
`)}`;

function CustomCursor({ cursorType = 'default', trailEnabled = true }) {
  const [trail, setTrail] = useState([]);
  const [bees, setBees] = useState([]); // Persistent bees for flower cursor (exactly 2-3)
  const [beeTrails, setBeeTrails] = useState({}); // Dotted trails for each bee
  const [isIdle, setIsIdle] = useState(false); // Mouse is still
  const [beeSpeedMultiplier, setBeeSpeedMultiplier] = useState(1); // Click increases this temporarily
  const trailIdRef = useRef(0);
  const beeIdRef = useRef(0);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const moveTimeoutRef = useRef(null);
  const idleTimeoutRef = useRef(null);
  const isFirstMoveRef = useRef(true);
  const beesInitializedRef = useRef(false);
  // Use ref for cursor position so bee animation doesn't restart on every mouse move
  const cursorPosRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

  // Handle mouse movement - create trail elements
  const handleMouseMove = useCallback((e) => {
    const newPos = { x: e.clientX, y: e.clientY };
    // Update ref (doesn't trigger re-render, bees read this directly)
    cursorPosRef.current = newPos;

    // Clear existing timeouts
    if (moveTimeoutRef.current) {
      clearTimeout(moveTimeoutRef.current);
    }
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    // Reset on mouse stop
    moveTimeoutRef.current = setTimeout(() => {
      isFirstMoveRef.current = true;
    }, 150);

    // Set idle state after 800ms of no movement (for flower/bees)
    setIsIdle(false);
    idleTimeoutRef.current = setTimeout(() => {
      setIsIdle(true);
    }, 800);

    // Only add trail if enabled and using custom cursor
    if (trailEnabled && (cursorType === 'pen' || cursorType === 'leaf' || cursorType === 'flower')) {
      const lastPos = lastPositionRef.current;
      const dx = newPos.x - lastPos.x;
      const dy = newPos.y - lastPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Different trail behavior for pen vs leaf vs flower
      if (cursorType === 'pen') {
        // Pen: Create line segments for ink trail
        if (distance > 3 && !isFirstMoveRef.current) {
          trailIdRef.current += 1;
          const angle = Math.atan2(dy, dx) * (180 / Math.PI);

          const newSegment = {
            id: trailIdRef.current,
            type: 'ink',
            x1: lastPos.x,
            y1: lastPos.y,
            x2: newPos.x,
            y2: newPos.y,
            angle: angle,
            opacity: 0.7,
            width: Math.max(1.5, Math.min(3, 2 + (distance - 3) * 0.02)),
          };

          setTrail(prev => [...prev.slice(-30), newSegment]);
        }
      } else if (cursorType === 'leaf') {
        // Leaf: Create falling leaf particles
        if (distance > 15 && !isFirstMoveRef.current) {
          trailIdRef.current += 1;

          // Random variations for natural look
          const randomColor = FALL_COLORS[Math.floor(Math.random() * FALL_COLORS.length)];
          const randomRotation = Math.random() * 360;
          const randomSize = 8 + Math.random() * 8; // 8-16px
          const randomOffsetX = (Math.random() - 0.5) * 10;
          const randomOffsetY = (Math.random() - 0.5) * 10;

          const newLeaf = {
            id: trailIdRef.current,
            type: 'leaf',
            x: newPos.x + randomOffsetX,
            y: newPos.y + randomOffsetY,
            rotation: randomRotation,
            size: randomSize,
            color: randomColor,
            opacity: 0.8,
            fallOffset: 0, // Will increase as leaf falls
          };

          setTrail(prev => [...prev.slice(-20), newLeaf]);
        }
      } else if (cursorType === 'flower') {
        // Flower cursor - bees are initialized in a separate useEffect, no spawning here
      }

      lastPositionRef.current = newPos;
      isFirstMoveRef.current = false;
    }
  }, [trailEnabled, cursorType]);

  // Initialize exactly 2-3 bees when flower cursor is selected
  useEffect(() => {
    if (cursorType === 'flower' && !beesInitializedRef.current) {
      beesInitializedRef.current = true;
      const numBees = 2 + Math.floor(Math.random() * 2); // 2 or 3 bees
      const initialBees = [];
      for (let i = 0; i < numBees; i++) {
        beeIdRef.current += 1;
        initialBees.push({
          id: beeIdRef.current,
          x: window.innerWidth / 2 + (Math.random() - 0.5) * 100,
          y: window.innerHeight / 2 + (Math.random() - 0.5) * 100,
          angle: Math.random() * 360,
          wobble: Math.random() * Math.PI * 2,
          orbitAngle: (i / numBees) * Math.PI * 2, // Spread them around
          size: 10 + Math.random() * 4,
          speed: 0.08 + Math.random() * 0.04,
        });
      }
      setBees(initialBees);
      // Initialize empty trails for each bee
      const trails = {};
      initialBees.forEach(bee => {
        trails[bee.id] = [];
      });
      setBeeTrails(trails);
    } else if (cursorType !== 'flower') {
      beesInitializedRef.current = false;
    }
  }, [cursorType]);

  // Fade out and animate trail elements
  useEffect(() => {
    if (trail.length === 0) return;

    const interval = setInterval(() => {
      setTrail(prev => {
        if (prev.length === 0) return prev;
        const updated = prev
          .map(item => {
            if (item.type === 'leaf') {
              // Leaves fade and fall
              return {
                ...item,
                opacity: item.opacity - 0.025,
                fallOffset: item.fallOffset + 1.5, // Gentle falling
                rotation: item.rotation + 2, // Gentle spinning
              };
            } else {
              // Ink fades
              return { ...item, opacity: item.opacity - 0.03 };
            }
          })
          .filter(item => item.opacity > 0);
        return updated;
      });
    }, 40);

    return () => clearInterval(interval);
  }, [trail.length]);

  // Animate bees - they always move smoothly toward a target orbit point
  // This effect runs independently of mouse movement since we use a ref
  useEffect(() => {
    if (cursorType !== 'flower') return;

    const interval = setInterval(() => {
      // Read cursor position from ref (doesn't cause effect to restart)
      const cursorPos = cursorPosRef.current;

      setBees(prev => {
        if (prev.length === 0) return prev;

        return prev.map(bee => {
          // Update orbit angle continuously (this determines where the bee WANTS to be)
          const orbitSpeed = 0.04 * beeSpeedMultiplier; // Faster when clicked
          const newOrbitAngle = bee.orbitAngle + orbitSpeed;

          // Calculate the target position (where the bee wants to orbit)
          const orbitRadius = 35 + Math.sin(bee.wobble * 0.5) * 15; // Varying orbit distance
          const targetX = cursorPos.x + Math.cos(newOrbitAngle) * orbitRadius;
          const targetY = cursorPos.y + Math.sin(newOrbitAngle) * orbitRadius;

          // Calculate direction to target
          const dx = targetX - bee.x;
          const dy = targetY - bee.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          // Smoothly move toward target (never teleport)
          // Higher speed multiplier = faster movement
          const baseSpeed = 4 + bee.speed * 30;
          const moveSpeed = baseSpeed * beeSpeedMultiplier;

          // Add wobble for natural movement
          const wobbleX = Math.sin(bee.wobble * 2) * 3 * beeSpeedMultiplier;
          const wobbleY = Math.cos(bee.wobble * 2.3) * 2 * beeSpeedMultiplier;

          let newX, newY;
          if (dist > 1) {
            // Move toward target with wobble
            const moveX = (dx / dist) * Math.min(moveSpeed, dist) + wobbleX;
            const moveY = (dy / dist) * Math.min(moveSpeed, dist) + wobbleY;
            newX = bee.x + moveX;
            newY = bee.y + moveY;
          } else {
            // Very close, just add wobble
            newX = bee.x + wobbleX;
            newY = bee.y + wobbleY;
          }

          // Calculate angle based on movement direction
          const actualMoveX = newX - bee.x;
          const actualMoveY = newY - bee.y;
          const moveAngle = Math.atan2(actualMoveY, actualMoveX) * (180 / Math.PI);

          return {
            ...bee,
            x: newX,
            y: newY,
            orbitAngle: newOrbitAngle,
            wobble: bee.wobble + 0.15 * beeSpeedMultiplier,
            angle: moveAngle + 90, // Bee points in direction of movement
          };
        });
      });

      // Update bee trails (dotted trail behind each bee)
      setBeeTrails(prev => {
        const newTrails = { ...prev };
        Object.keys(newTrails).forEach(beeId => {
          // We need to get current bee position from state
          // Since we're in the same interval, we'll update trails based on prev state
        });
        return newTrails;
      });
    }, 30);

    return () => clearInterval(interval);
  }, [cursorType, beeSpeedMultiplier]);

  // Separate effect for updating bee trails to avoid stale closure issues
  useEffect(() => {
    if (cursorType !== 'flower' || bees.length === 0) return;

    const interval = setInterval(() => {
      setBeeTrails(prev => {
        const newTrails = { ...prev };
        bees.forEach(bee => {
          if (!newTrails[bee.id]) newTrails[bee.id] = [];
          // Add new trail point
          newTrails[bee.id] = [
            ...newTrails[bee.id].slice(-8), // Keep last 8 dots
            { x: bee.x, y: bee.y, opacity: 1 }
          ];
          // Fade existing dots
          newTrails[bee.id] = newTrails[bee.id].map((dot, i) => ({
            ...dot,
            opacity: (i + 1) / newTrails[bee.id].length * 0.6
          }));
        });
        return newTrails;
      });
    }, 30);

    return () => clearInterval(interval);
  }, [cursorType, bees]);

  // Handle click - make bees speed up temporarily (buzz faster in their orbit)
  const handleClick = useCallback(() => {
    if (cursorType !== 'flower' || bees.length === 0) return;

    // Speed up the bees
    setBeeSpeedMultiplier(3);

    // Return to normal speed after 800ms
    setTimeout(() => {
      setBeeSpeedMultiplier(1);
    }, 800);
  }, [cursorType, bees.length]);

  // Add click listener for bee buzz
  useEffect(() => {
    if (cursorType === 'flower') {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [cursorType, handleClick]);

  // Clear bees and trails when switching away from flower cursor
  useEffect(() => {
    if (cursorType !== 'flower') {
      setBees([]);
      setBeeTrails({});
    }
  }, [cursorType]);

  // Add and remove mouse move listener
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }
    };
  }, [handleMouseMove]);

  // Set cursor style on body based on cursor type
  useEffect(() => {
    const cursorUrl = cursorType === 'pen' ? FOUNTAIN_PEN_CURSOR :
                      cursorType === 'leaf' ? LEAF_CURSOR :
                      cursorType === 'flower' ? FLOWER_CURSOR :
                      'default';

    // Hotspot position varies by cursor type
    const hotspotX = cursorType === 'flower' ? 16 : 28;
    const hotspotY = cursorType === 'flower' ? 32 : 28;

    if (cursorType === 'default') {
      document.body.style.cursor = 'default';
      // Remove any custom cursor styles
      const existingStyle = document.getElementById('custom-cursor-style');
      if (existingStyle) {
        existingStyle.remove();
      }
      return;
    }

    // Position hotspot at the tip
    document.body.style.cursor = `url("${cursorUrl}") ${hotspotX} ${hotspotY}, auto`;

    // Apply cursor to all elements
    const style = document.createElement('style');
    style.id = 'custom-cursor-style';
    style.textContent = `
      * {
        cursor: url("${cursorUrl}") ${hotspotX} ${hotspotY}, auto !important;
      }
      a, button, [role="button"], input[type="submit"], input[type="button"] {
        cursor: url("${cursorUrl}") ${hotspotX} ${hotspotY}, pointer !important;
      }
      input, textarea, [contenteditable] {
        cursor: url("${cursorUrl}") ${hotspotX} ${hotspotY}, text !important;
      }
    `;

    // Remove existing style if any
    const existingStyle = document.getElementById('custom-cursor-style');
    if (existingStyle) {
      existingStyle.remove();
    }

    document.head.appendChild(style);

    return () => {
      document.body.style.cursor = '';
      const styleEl = document.getElementById('custom-cursor-style');
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, [cursorType]);

  // Don't render trail elements if disabled or default cursor
  // But always render for flower cursor if there are bees
  if (cursorType === 'default') {
    return null;
  }
  if (!trailEnabled && cursorType !== 'flower') {
    return null;
  }
  if (cursorType === 'flower' && bees.length === 0 && !trailEnabled) {
    return null;
  }

  return (
    <div className="cursor-trail-container">
      <svg className="ink-trail-svg" width="100%" height="100%">
        <defs>
          {/* Gradient for ink color variation */}
          <linearGradient id="inkGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2c3e50" />
            <stop offset="100%" stopColor="#1a252f" />
          </linearGradient>
        </defs>

        {/* Render ink lines for pen cursor */}
        {trail.filter(item => item.type === 'ink').map(seg => (
          <line
            key={seg.id}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            className="ink-line"
            style={{
              strokeWidth: seg.width,
              opacity: seg.opacity,
            }}
          />
        ))}

        {/* Render falling leaves for leaf cursor */}
        {trail.filter(item => item.type === 'leaf').map(leaf => (
          <g
            key={leaf.id}
            transform={`translate(${leaf.x}, ${leaf.y + leaf.fallOffset}) rotate(${leaf.rotation}, ${leaf.size / 2}, ${leaf.size / 2})`}
            style={{ opacity: leaf.opacity }}
          >
            {/* Mini leaf shape */}
            <path
              d={`M 0 ${leaf.size} Q ${leaf.size * 0.2} ${leaf.size * 0.5}, ${leaf.size * 0.5} ${leaf.size * 0.2} Q ${leaf.size * 0.8} ${leaf.size * 0.2}, ${leaf.size} 0 Q ${leaf.size * 0.8} ${leaf.size * 0.4}, ${leaf.size * 0.5} ${leaf.size * 0.7} Q ${leaf.size * 0.2} ${leaf.size}, 0 ${leaf.size}`}
              fill={leaf.color}
              stroke="#5C4033"
              strokeWidth="0.5"
            />
            {/* Leaf vein */}
            <path
              d={`M 0 ${leaf.size} Q ${leaf.size * 0.4} ${leaf.size * 0.5}, ${leaf.size} 0`}
              fill="none"
              stroke="#5C4033"
              strokeWidth="0.4"
              opacity="0.6"
            />
          </g>
        ))}

        {/* Render dotted trails for bees */}
        {Object.entries(beeTrails).map(([beeId, trailDots]) => (
          <g key={`trail-${beeId}`}>
            {trailDots.map((dot, i) => (
              <circle
                key={`dot-${beeId}-${i}`}
                cx={dot.x}
                cy={dot.y}
                r={2}
                fill="#FFD700"
                opacity={dot.opacity}
              />
            ))}
          </g>
        ))}

        {/* Render bees for flower cursor */}
        {bees.map(bee => (
          <g
            key={bee.id}
            transform={`translate(${bee.x - bee.size / 2}, ${bee.y - bee.size / 2}) rotate(${bee.angle}, ${bee.size / 2}, ${bee.size / 2})`}
          >
            {/* Bee body - yellow with black stripes */}
            <ellipse
              cx={bee.size / 2}
              cy={bee.size / 2}
              rx={bee.size * 0.35}
              ry={bee.size * 0.45}
              fill="#FFD700"
            />
            {/* Black stripes */}
            <rect
              x={bee.size * 0.2}
              y={bee.size * 0.35}
              width={bee.size * 0.6}
              height={bee.size * 0.12}
              fill="#222"
            />
            <rect
              x={bee.size * 0.2}
              y={bee.size * 0.55}
              width={bee.size * 0.6}
              height={bee.size * 0.12}
              fill="#222"
            />
            {/* Bee head */}
            <circle
              cx={bee.size / 2}
              cy={bee.size * 0.15}
              r={bee.size * 0.18}
              fill="#222"
            />
            {/* Wings - animated flutter */}
            <ellipse
              cx={bee.size * 0.2}
              cy={bee.size * 0.4}
              rx={bee.size * 0.25}
              ry={bee.size * 0.15}
              fill="rgba(200, 220, 255, 0.6)"
              transform={`rotate(${Math.sin(bee.wobble * 8) * 20}, ${bee.size * 0.2}, ${bee.size * 0.4})`}
            />
            <ellipse
              cx={bee.size * 0.8}
              cy={bee.size * 0.4}
              rx={bee.size * 0.25}
              ry={bee.size * 0.15}
              fill="rgba(200, 220, 255, 0.6)"
              transform={`rotate(${-Math.sin(bee.wobble * 8) * 20}, ${bee.size * 0.8}, ${bee.size * 0.4})`}
            />
            {/* Stinger */}
            <path
              d={`M ${bee.size / 2} ${bee.size * 0.95} L ${bee.size / 2} ${bee.size * 1.05}`}
              stroke="#222"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </g>
        ))}
      </svg>
    </div>
  );
}

export default CustomCursor;
